/**
 * Feuille d'actions — EF-NAV-006.
 *
 * Elle réunit toutes les commandes du roadbook. Le bouton qui l'ouvre est
 * permanent, explicitement libellé et toujours au même endroit : ce n'est ni
 * un bouton « … », ni un menu au contenu variable. Son inventaire est fixe ;
 * une commande sans objet y est désactivée avec sa raison, jamais retirée.
 */

import type { JSX } from 'preact';
import { CATEGORIES, categoryLabel } from '../../core/categories';
import { isOffline } from '../../platform/offline';
import { Glyph, type GlyphName } from '../icons';
import { ACTIONS, ERRORS, SHEETS, SHEET_HINTS } from '../labels';
import { Sheet } from './Sheet';
import {
  busy,
  chooseRoadbookFile,
  closeSheet,
  document_,
  editingPoiId,
  exportRoadbook,
  openSheet,
  parameters,
  readOnly,
  screen,
} from '../state';

export function ActionSheet(): JSX.Element {
  const current = document_.value;
  const operation = busy.value;
  const offline = isOffline();

  const raceReason =
    current === null
      ? 'Aucun roadbook chargé.'
      : current.results.totals === null
        ? ERRORS.raceNeedsResult
        : operation !== null
          ? ERRORS.raceDuringOperation
          : null;

  const listReason = current === null ? 'Aucun roadbook chargé.' : null;

  return (
    <Sheet title={SHEETS.actions} onClose={closeSheet}>
      <Entry
        glyph="course"
        label="Mode course"
        hint={SHEET_HINTS.race}
        disabledReason={raceReason}
        onSelect={() => {
          closeSheet();
          screen.value = 'race';
        }}
      />
      <Entry
        glyph="navigation"
        label={SHEETS.goto}
        hint={SHEET_HINTS.goto}
        disabledReason={listReason}
        onSelect={() => {
          openSheet('goto');
        }}
      />
      <Entry
        glyph="calculer"
        label={ACTIONS.recalcWithNewStart}
        hint={SHEET_HINTS.recalc}
        disabledReason={listReason ?? (readOnly.value ? ERRORS.readOnlyContext : null)}
        onSelect={() => {
          openSheet('recalc');
        }}
      />

      <h3 class="sheet__group">Roadbook</h3>

      <Entry
        glyph="importer"
        label={ACTIONS.importRoadbook}
        disabledReason={readOnly.value ? ERRORS.readOnlyContext : null}
        onSelect={() => {
          closeSheet();
          void chooseRoadbookFile();
        }}
      />
      <Entry
        glyph="exporter"
        label="Exporter le roadbook"
        disabledReason={listReason}
        onSelect={() => {
          closeSheet();
          void exportRoadbook();
        }}
      />
      <Entry
        glyph="ajouter"
        label={ACTIONS.addManualPoi}
        disabledReason={listReason ?? (readOnly.value ? ERRORS.readOnlyContext : null)}
        onSelect={() => {
          editingPoiId.value = null;
          openSheet('manualPoi');
        }}
      />
      <Entry
        glyph="filtres"
        label={SHEETS.filters}
        hint={describeFilters()}
        disabledReason={listReason}
        onSelect={() => {
          openSheet('filters');
        }}
      />

      {offline ? <p class="notice">{ERRORS.searchOffline}</p> : null}
    </Sheet>
  );
}

function Entry(props: {
  readonly glyph: GlyphName;
  readonly label: string;
  readonly hint?: string;
  /** Non nul : l'entrée reste visible mais désactivée, avec sa raison. */
  readonly disabledReason: string | null;
  readonly onSelect: () => void;
}): JSX.Element {
  const disabled = props.disabledReason !== null;
  return (
    <button type="button" class="sheet__item" disabled={disabled} onClick={props.onSelect}>
      <Glyph name={props.glyph} label="" size={26} decorative />
      <span class="sheet__item-text">
        <span>{props.label}</span>
        {props.disabledReason !== null ? (
          <span class="sheet__item-hint">{props.disabledReason}</span>
        ) : props.hint !== undefined ? (
          <span class="sheet__item-hint">{props.hint}</span>
        ) : null}
      </span>
      {disabled ? null : <Glyph name="suivant" label="" size={22} decorative />}
    </button>
  );
}

/** État courant du filtrage, rappelé en sous-titre de l'entrée « Filtres ». */
function describeFilters(): string {
  const display = parameters.value.display;
  const hidden = display.hiddenCategories.length;
  const parts: string[] = [];
  if (hidden === 0) parts.push('toutes les catégories affichées');
  else if (hidden === CATEGORIES.length) parts.push('toutes les catégories masquées');
  else if (hidden === 1) {
    const key = display.hiddenCategories[0];
    parts.push(`${key === undefined ? '1 catégorie' : categoryLabel(key)} masquée`);
  } else parts.push(`${hidden} catégories masquées`);
  if (display.pinnedOnly) parts.push('épinglés seulement');
  if (display.excludedOnly) parts.push('exclus seulement');
  return parts.join(' · ');
}

